/**
 * Load and display spin-offs of a scratchpad.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {useGql} from "@repo/data-gql";
import {PermissionsInfo} from "@repo/shared-core";

import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import DynamicProjectList from "./dynamic-project-list.tsx";
import {TopSpinoffProjectsFetcher} from "./project-list-fetchers/top-spinoff-projects-fetcher.ts";
import {
    UserSpinoffsOfProgramFetcher,
    type GqlSpinoffOriginKind,
} from "./project-list-fetchers/user-spinoffs-of-program-fetcher.ts";

type Props = {
    // The ID of the scratchpad. In the case of a user-created scratchpad
    // or old-style in-content-tree scratchpad this'll be an integer we've
    // casted to a string, otherwise it'll be a content ID like
    // "xdb7ed4515bb5868".
    scratchpadId: string;
    // The kind of the scratchpad. In the case of a user-created scratchpad
    // this'll be "Scratchpad", otherwise it'll be the content kind like
    // "Talkthrough". It may also be falsy, in which case we won't mention
    // a kind to the server (it'll assume user-created Scratchpad in that
    // case).
    scratchpadKind?: string;
} & WithUserProfileProps;

function ScratchpadSpinOffs(props: Props): React.ReactElement {
    const gqlFetch = useGql();

    function getSpinoffOriginKindForGraphQL(
        kind?: string | null,
    ): GqlSpinoffOriginKind {
        switch (kind) {
            case "Challenge":
                return "CHALLENGE";
            case "Interactive":
                return "INTERACTIVE";
            case "Project":
                return "PROJECT";
            case "Talkthrough":
                return "TALKTHROUGH";
            default:
                return "PROGRAM";
        }
    }

    function getSpinoffOrigin() {
        const {scratchpadId: id, scratchpadKind: kind} = props;
        return {
            kind: getSpinoffOriginKindForGraphQL(kind),
            programOrContentId: id,
        };
    }

    const renderSpinOffs: () => React.ReactNode = () => {
        const noResults = <p>{t`Be the first to create a spin-off!`}</p>;
        return (
            <div className={css(styles.spinOffs)}>
                <DynamicProjectList
                    title={t`All Spin-offs`}
                    projectListFetcher={
                        new TopSpinoffProjectsFetcher({
                            gqlFetch,
                            spinoffOrigin: getSpinoffOrigin(),
                            itemsPerPage: 30,
                        })
                    }
                    noResults={() => noResults}
                />
            </div>
        );
    };

    const renderUserSpinOffs: () => React.ReactNode = () => {
        const {profile} = props;
        let noResults:
            | React.ReactElement<React.ComponentProps<"div">>
            | React.ReactElement<React.ComponentProps<"p">> = (
            <p>{t`Login to view your spin-offs of this project.`}</p>
        );

        if (PermissionsInfo.isLoggedIn && profile) {
            const projectsURL = profile.get("profileRoot") + "projects";

            noResults = (
                <div>
                    <p>
                        {t`If you save a spin-off of this project, you will see it here (as well as in your My Projects list).`}
                    </p>
                    <p>
                        <Button
                            href={projectsURL}
                            kind="secondary"
                            target="_blank"
                        >
                            {t`Browse My Projects`}
                        </Button>
                    </p>
                </div>
            );
        }

        return (
            <div>
                <DynamicProjectList
                    title={t`Your Spin-offs`}
                    noResults={() => noResults}
                    hideNotice={true}
                    projectListFetcher={
                        new UserSpinoffsOfProgramFetcher({
                            gqlFetch,
                            spinoffOrigin: getSpinoffOrigin(),
                            itemsPerPage: 30,
                        })
                    }
                />
            </div>
        );
    };

    return (
        <div>
            {renderUserSpinOffs()}
            {renderSpinOffs()}
        </div>
    );
}

const styles = StyleSheet.create({
    spinOffs: {
        marginTop: 22,
    },
});

type ExportProps = WithoutUserProfile<
    JSX.LibraryManagedAttributes<
        typeof ScratchpadSpinOffs,
        React.ComponentProps<typeof ScratchpadSpinOffs>
    >
>;

export default withUserProfile(
    ScratchpadSpinOffs,
) as React.ComponentType<ExportProps>;
